import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import { useTheme } from "@mui/material/styles";
import React, { useState } from "react";

import {
  DarkMode,
  LightMode,
  Logout,
  MoreVert,
  QuestionAnswer,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";

import { useThemeMode } from "@/context/Theme";

interface UserMenuProps {
  isMenuExpanded: boolean;
}

const UserMenu = ({ isMenuExpanded }: UserMenuProps) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const { toggleDarkMode } = useThemeMode();
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuth0();
  const queryClient = useQueryClient();

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    // Don't leave this user's data in the cache
    queryClient.clear();
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    }).then();
  };

  const handleContactSupport = () => {
    window.location.href = "mailto:support@starcount.com";
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {user?.picture && (
        <img
          src={user.picture}
          alt={user.name}
          style={{
            borderRadius: "50%",
            cursor: !isMenuExpanded ? "pointer" : "default",
          }}
          width="38"
          height="38"
          onClick={!isMenuExpanded ? handleOpenUserMenu : undefined}
        />
      )}
      {isMenuExpanded && (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              ml: 1,
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {user?.given_name} {user?.family_name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                flexWrap: "wrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "150px",
              }}
            >
              {user?.email}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleOpenUserMenu} sx={{ ml: 1 }}>
            <MoreVert />
          </IconButton>
        </>
      )}
      <Menu
        id="user-settings-menu"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        <MenuItem onClick={toggleDarkMode}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {mode === "dark" ? <LightMode /> : <DarkMode />}
            <Typography sx={{ ml: 1 }} textAlign="center">
              {`Set ${mode === "dark" ? "Light" : "Dark"} Mode`}
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleContactSupport}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <QuestionAnswer />
            <Typography sx={{ ml: 1 }} textAlign="center">
              Contact Support
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Logout />
            <Typography sx={{ ml: 1 }} textAlign="center">
              Logout
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UserMenu;
